import { Link } from 'react-router-dom';
import { useLocalizedPath } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';
import googleThumbs from '../../public/images/google/thumbs.json';

interface EntityCardProps {
  entity: any;
  category: string;
}

export function EntityCard({ entity, category }: EntityCardProps) {
  const localize = useLocalizedPath();
  const { t } = useTranslation();
  const { trackEntityView } = useAnalytics();
  const e = entity;
  const handle = e.handle as string;
  const title = e.title as string;
  const subtitle = e.subtitle as string;
  const image = e.image as string;
  const highlights = (e.highlights as string[]) || [];
  const rating = e.rating as number | undefined;
  const reviewCount = e.reviewCount as number | undefined;
  const isPartner = !!e.provider?.commission;

  const thumbs = googleThumbs as Record<string, string>;
  const cardImage = thumbs[handle] || image || '/images/hero-bg.jpg';

  return (
    <Link
      to={localize(`/${category}/${handle}`)}
      onClick={() => trackEntityView({ entityType: category, entityName: title, entityId: handle, category, partner: isPartner })}
      className="group block bg-white rounded-2xl overflow-hidden border border-sand-200 shadow-[0_2px_12px_rgba(0,0,0,0.06)] card-hover-shadow"
    >
      <div className="relative h-48 card-image-container">
        <picture className="block w-full h-full">
          <source srcSet={cardImage.replace(/\.(jpe?g|png)$/i, '.webp')} type="image/webp" />
          <img src={cardImage} alt={title} className="w-full h-full object-cover card-image-zoom" loading="lazy" decoding="async" width="400" height="225" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        {isPartner && (
          <span className="absolute top-3 right-3 bg-coral text-white text-xs font-body font-bold px-2.5 py-1 rounded-full">
            {t('entity.card.partnerBadge')}
          </span>
        )}
        {e.difficulty && (
          <span className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm text-warm-dark font-body text-xs font-semibold px-2.5 py-1 rounded-full">
            {e.difficulty}
          </span>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-display text-lg text-warm-dark group-hover:text-teal-deep transition-colors leading-tight tracking-wide">
            {title.toUpperCase()}
          </h3>
          {typeof rating === 'number' && rating > 0 && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-body text-sm font-semibold text-amber-600">{rating}</span>
              {typeof reviewCount === 'number' && (
                <span className="font-body text-xs text-warm-gray">({reviewCount})</span>
              )}
            </div>
          )}
        </div>

        <p className="font-body text-sm text-warm-gray mb-3 line-clamp-2">{subtitle}</p>

        {highlights.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {highlights.slice(0, 2).map((h: string) => (
              <span
                key={h}
                className="font-body text-xs bg-teal-light/10 text-teal-deep border border-teal-light/20 px-2 py-0.5 rounded-full"
              >
                {h.split('—')[0].trim()}
              </span>
            ))}
          </div>
        )}

        {e.price?.amount && (
          <div className="mt-3 pt-3 border-t border-sand-200">
            <span className="font-display text-lg text-coral">R$ {e.price.amount}</span>
            <span className="font-body text-sm text-warm-gray ml-1">/{e.price.unit}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
